import type { default as LessonHistoryClient } from '@whizz/api-client/dist/@types/model/tx3/LessonHistory';
import type { DateRange } from '../../util/types';
import { model } from '@whizz/api-client';
import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { dateTimeToDateString } from '../../util/dates';
import { QUERIES_IDS } from '../../util/vars';
import { LessonHistoryTable } from '../LessonHistory/modules';
import { DateTime } from 'luxon';
import { useSwishPanel } from '../../hooks/useSwishPanel';
import { Button, ButtonIcon, Loader } from '../../components';
import { Header } from '../../layouts/common';
import { useI18n } from '@whizz/react-i18n';
import { SWISHPANEL_MASK } from '../../util/data-id';
import { useMixpanel } from 'react-mixpanel-browser';

interface Props {
  student: { childId: number, fullName: string };
  groupId: number;
  dateRange: DateRange;
  onClose: () => void;
  /**
   * Hides the green Whizz header bar. Only passed as `true` from the Class Report > table view
   * entry point (TablesView.tsx) - this panel is layered directly over that table view, so
   * showing the header/Menu there is redundant; the Home > Your Students entry point keeps it.
   */
  hideHeader?: boolean;
}

interface LessonHistoryProps {
  history: LessonHistoryClient[];
  studentName: string;
  loading: boolean;
  groupId: number;
  hideHeader?: boolean;
}

const LessonHistory = ({ student, groupId, dateRange, onClose, hideHeader }: Props): JSX.Element => {

  const mixpanel = useMixpanel();
  const { locale } = useI18n();

  const lessonHistoryQuery = useQuery<LessonHistoryClient[]>(
    [
      QUERIES_IDS.LESSON_HISTORY,
      student.childId,
      groupId,
      dateTimeToDateString(dateRange.startDate),
      dateTimeToDateString(dateRange.endDate),
    ],
    () => model.LessonHistory.manyByStudentId(
      student.childId,
      groupId,
      locale?.id,
      dateRange.startDate ?? DateTime.now(), //TODO: Temp
      dateRange.endDate ? dateRange.endDate : undefined,
    ),
    {
      enabled: null != groupId && null != student.childId,
      placeholderData: [],
      cacheTime: 0,
    },
  );

  const isLoading = lessonHistoryQuery.isFetching || !lessonHistoryQuery.isFetched;

  useEffect(() => {
    if (!mixpanel || !mixpanel?.config?.token) {
      return;
    }
    mixpanel.track('Lesson History Panel');
  }, [ mixpanel ]);

  return (
    <Table studentName={student.fullName} history={lessonHistoryQuery.data ?? []} loading={isLoading} onClose={onClose} groupId={groupId} hideHeader={hideHeader} />
  );
};

const Table = ({ history, loading, studentName, groupId, hideHeader, onClose }: LessonHistoryProps & Pick<Props, 'onClose'>) => {

  const { translate } = useI18n();
  const handleClose = () => toggle(false);

  const [ swishPanel, toggle, setProps ] = useSwishPanel(({ history, loading = true, studentName, groupId, hideHeader }: LessonHistoryProps) => (
    <div>
      { hideHeader || <Header classId={groupId}>{null}</Header> }
      <div className={'border-b sticky top-0 z-max bg-white rounded-t-default'} data-id='lesson-history-header'>
        <div className={'p-md grid grid-cols-5 md:flex justify-between items-center'}>
          <div>
            <div className={'md:hidden'}>
              <ButtonIcon
                isOutline
                size={'sm'}
                icon={'ArrowBack'}
                onClick={handleClose}
              />
            </div>
            <div className={'hidden md:block'}>
              <Button
                isOutline
                icon={'ArrowBack'}
                onClick={handleClose}
              >
                {translate('components.BUTTONS.BACK')}
              </Button>
            </div>
          </div>
          <div className={'flex justify-center col-span-3'}>
            <h2 className={'border-b-4 border-header p-1 w-max font-bold whitespace-nowrap text-xl md:text-2xl'}>{translate('global.ROUTES.LESSON_HISTORY')}</h2>
          </div>
          <div className={'col-span-5 text-center font-bold italic rounded-default px-2 py-1 bg-white border-3 text-primary border-primary mt-md md:mt-0'}>
            {studentName}
          </div>
        </div>
      </div>
      <div className={'bg-light min-h-full'}>
        <div className={'max-w-screen-xl w-full mx-auto px-md'}>
          <div className={'container mt-8 pt-0'}>
            { loading ? <Loader /> : <LessonHistoryTable history={history} scrollContainer={document.querySelector(`[data-id='${SWISHPANEL_MASK}']`)} stickyTopSelector={'[data-id="lesson-history-header"]'} /> }
          </div>
        </div>
      </div>
    </div>
  ), undefined,
  onClose,
  );

  useEffect(() => {
    toggle(true);
    setProps({
      history: history,
      studentName: studentName,
      loading: loading,
      groupId: groupId,
      hideHeader: hideHeader,
    });
  }, [ studentName, history, loading, groupId, hideHeader ]);

  return (
    <div>
      {swishPanel}
    </div>
  );
};

LessonHistory.displayName = 'LessonHistory';

export default LessonHistory;
